import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useCallback, useEffect, useState } from 'react';
import { View, FlatList, TouchableOpacity, Keyboard, Dimensions, StyleSheet, Platform } from 'react-native';

import { CommandResultItem, type Command } from '@/components/commands';
import { EnhancedRefreshControl } from '@/components/common/enhanced-refresh-control';
import { Text, useTheme, Searchbar, Modal } from '@/components/ui';
import { useCommands } from '@/hooks/use-commands';
import { useGlobalSearch } from '@/hooks/use-global-search';
import { useIsLargeScreen } from '@/hooks/use-is-large-screen';
import { usePullToRefresh } from '@/hooks/use-pull-to-refresh';
import { dataService } from '@/services/data-service';
import { listStyles } from '@/styles';

export interface GlobalSearchResult {
	id: string;
	type: 'work-order' | 'association' | 'user' | 'inspection-report' | 'person' | 'account' | 'vendor';
	title: string;
	subtitle?: string;
	description?: string;
	metadata?: Record<string, unknown>;
}

export type SearchItem = GlobalSearchResult | Command;


// Search result item component
const SearchResultItem: React.FC<{
	result: GlobalSearchResult;
	isSelected?: boolean;
	onPress: () => void;
}> = ({ result, isSelected = false, onPress }) => {
	const theme = useTheme();

	const getIcon = () => {
		switch (result.type) {
			case 'work-order':
				return 'document-text-outline';
			case 'association':
				return 'business-outline';
			case 'user':
				return 'person-outline';
			case 'inspection-report':
				return 'camera-outline';
			case 'person':
				return 'person-circle-outline';
			case 'account':
				return 'home-outline';
			case 'vendor':
				return 'construct-outline';
			default:
				return 'search-outline';
		}
	};

	const getTypeLabel = () => {
		switch (result.type) {
			case 'work-order':
				return 'Work Order';
			case 'association':
				return 'Association';
			case 'user':
				return 'User';
			case 'inspection-report':
				return 'Inspection Report';
			case 'person':
				return 'Person';
			case 'account':
				return 'Account';
			case 'vendor':
				return 'Vendor';
			default:
				return 'Result';
		}
	};

	return (
		<TouchableOpacity
			onPress={onPress}
			style={[
				styles.listItem,
				{
					backgroundColor: isSelected ? theme.colors.primaryContainer : theme.colors.surface,
					borderBottomColor: theme.colors.outline,
				},
			]}
		>
			<View style={styles.listItemContent}>
				<View style={[
					styles.listItemIcon,
					{
						backgroundColor: isSelected ? theme.colors.primary : '#f5f5f5',
					},
				]}>
					<Ionicons
						name={getIcon() as keyof typeof Ionicons.glyphMap}
						size={24}
						color={isSelected ? theme.colors.onPrimary : theme.colors.primary}
					/>
				</View>
				<View style={styles.listItemText}>
					<Text
						variant="bodyLarge"
						style={{
							fontWeight: '600',
							color: isSelected ? theme.colors.onPrimaryContainer : theme.colors.onSurface,
						}}
					>
						{result.title}
					</Text>
					{result.subtitle && (
						<Text
							variant="bodyMedium"
							style={{
								color: isSelected ? theme.colors.onPrimaryContainer : theme.colors.onSurfaceVariant,
							}}
						>
							{result.subtitle}
						</Text>
					)}
					<View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
						<Text
							variant="labelSmall"
							style={{
								color: isSelected ? theme.colors.primary : theme.colors.primary,
							}}
						>
							{getTypeLabel()}
						</Text>
						{result.description && (
							<>
								<Text
									variant="labelSmall"
									style={{
										color: isSelected ? theme.colors.onPrimaryContainer : theme.colors.onSurfaceVariant,
										marginHorizontal: 8,
									}}
								>
									•
								</Text>
								<Text
									variant="labelSmall"
									style={{
										color: isSelected ? theme.colors.onPrimaryContainer : theme.colors.onSurfaceVariant,
									}}
								>
									{result.description}
								</Text>
							</>
						)}
					</View>
				</View>
			</View>
		</TouchableOpacity>
	);
};

// Main global search popup component
export const GlobalSearchPopup: React.FC = () => {
	const [loading, setLoading] = useState(false);
	const [results, setResults] = useState<GlobalSearchResult[]>([]);
	const [searchQuery, setSearchQuery] = useState('');
	const [debouncedQuery, setDebouncedQuery] = useState('');
	const { refreshing, onRefresh } = usePullToRefresh();
	const theme = useTheme();
	const { isSearchVisible, closeSearch, openSearch } = useGlobalSearch();
	const screenHeight = Dimensions.get('window').height;
	const isLargeScreen = useIsLargeScreen();
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [isCommandMode, setIsCommandMode] = useState(false);
	const [commandResults, setCommandResults] = useState<Command[]>([]);

	// Use the command system
	const { processCommandQuery } = useCommands();

	// Check if query is a command
	useEffect(() => {
		if (searchQuery.startsWith('/')) {
			setIsCommandMode(true);
			const commands = processCommandQuery(searchQuery);
			setCommandResults(commands);
		} else {
			setIsCommandMode(false);
			setCommandResults([]);
		}
	}, [searchQuery, processCommandQuery]);

	// Debounce search query for regular search
	useEffect(() => {
		if (!isCommandMode) {
			const timer = setTimeout(() => {
				setDebouncedQuery(searchQuery);
			}, 300);

			return () => clearTimeout(timer);
		}
	}, [searchQuery, isCommandMode]);

	const keyDownHandler = (event: KeyboardEvent) => {
		if (
			event.key === 'k'
			&& (event.metaKey || event.altKey)
			&& !isSearchVisible
		) {
			event.preventDefault();
			openSearch();
		} else if (isSearchVisible) {
			handleKeyPress({ nativeEvent: event });
		}
	};

	useEffect(() => {
		if (Platform.OS === 'web') {
			addEventListener('keydown', keyDownHandler);
			return () => {
				removeEventListener('keydown', keyDownHandler);
			};
		}
		// TODO: Add iOS and Android key down handlers?
	// eslint-disable-next-line react-hooks/exhaustive-deps -- keyDownHandler is inline and captures current state via closure; mount-only listener
	}, []);

	// Perform search when debounced query changes (only in non-command mode)
	useEffect(() => {
		if (isCommandMode) {
			setResults([]);
			return;
		}

		if (debouncedQuery.trim().length < 2) {
			setResults([]);
			return;
		}

		const performSearch = async () => {
			try {
				setLoading(true);
				const response = await dataService.globalSearch(debouncedQuery, { limit: 20 });
				setResults(response.data || []);
			} catch (error) {
				console.error('Global search error:', error);
				setResults([]);
			} finally {
				setLoading(false);
			}
		};

		performSearch();
	}, [debouncedQuery, isCommandMode]);

	// Reset selected index when results change
	useEffect(() => {
		setSelectedIndex(0);
	}, [results, commandResults]);

	const handleItemPress = useCallback((result: GlobalSearchResult) => {
		// Navigate based on result type
		switch (result.type) {
			case 'work-order':
				router.push({ pathname: '/work-orders/[id]', params: { id: result.id } });
				break;
			case 'association':
				router.push({ pathname: '/associations/[id]', params: { id: result.id } });
				break;
			case 'user':
				router.push('/users');
				break;
			case 'inspection-report':
				router.push({ pathname: '/inspections/[id]', params: { id: result.id } });
				break;
			case 'account':
				router.push({ pathname: '/accounting/account/[id]', params: { id: result.id } });
				break;
			case 'vendor':
				router.push({ pathname: '/accounting/vendor/[id]', params: { id: result.id } });
				break;
			case 'person': {
				// People don't have a dedicated detail page yet; jump to their
				// first linked account (the most useful destination when
				// identifying a caller). Fall back to closing the popup.
				const accountId = result.metadata?.firstAccountId as string | undefined;
				if (accountId) {
					router.push({ pathname: '/accounting/account/[id]', params: { id: accountId } });
				}
				break;
			}
		}
		closeSearch();
	}, [closeSearch]);

	const handleCommandPress = useCallback((command: Command) => {
		command.action(closeSearch);
	}, [closeSearch]);

	// Handle modal close
	const handleClose = useCallback(() => {
		setSearchQuery('');
		setResults([]);
		setCommandResults([]);
		setIsCommandMode(false);
		closeSearch();
		Keyboard.dismiss();
	}, [closeSearch]);

	const handleKeyPress = useCallback((event: { nativeEvent: { key: string; preventDefault?: () => void } }) => {
		const key = event.nativeEvent.key;
		const currentResults = isCommandMode ? commandResults : results;

		if (key === 'ArrowDown') {
			event.nativeEvent.preventDefault?.();
			setSelectedIndex((prevIndex) => {
				if (currentResults.length === 0) return 0;
				return prevIndex < currentResults.length - 1 ? prevIndex + 1 : 0;
			});
		} else if (key === 'ArrowUp') {
			event.nativeEvent.preventDefault?.();
			setSelectedIndex((prevIndex) => {
				if (currentResults.length === 0) return 0;
				return prevIndex > 0 ? prevIndex - 1 : currentResults.length - 1;
			});
		} else if (key === 'Enter') {
			event.nativeEvent.preventDefault?.();
			if (currentResults.length > 0 && selectedIndex >= 0 && selectedIndex < currentResults.length) {
				if (isCommandMode) {
					handleCommandPress(commandResults[selectedIndex]);
				} else {
					handleItemPress(results[selectedIndex]);
				}
			}
		} else if (key === 'Escape') {
			event.nativeEvent.preventDefault?.();
			handleClose();
		}
	}, [results, commandResults, selectedIndex, handleItemPress, handleCommandPress, handleClose, isCommandMode]);

	// Handle refresh
	const handleRefresh = useCallback(async () => {
		if (isCommandMode) {
			// Refresh command results by re-running the command filter
			const commands = processCommandQuery(searchQuery);
			setCommandResults(commands);
		} else if (debouncedQuery.trim().length >= 2) {
			try {
				setLoading(true);
				const response = await dataService.globalSearch(debouncedQuery, { limit: 20 });
				setResults(response.data || []);
			} catch (error) {
				console.error('Global search refresh error:', error);
			} finally {
				setLoading(false);
			}
		}
		onRefresh();
	}, [debouncedQuery, onRefresh, isCommandMode, searchQuery, processCommandQuery]);

	// Handle search query change
	const handleSearchChange = useCallback((query: string) => {
		setSearchQuery(query);
	}, []);

	// Empty state component
	const EmptyState = () => {
		if (isCommandMode) {
			return (
				<View style={[listStyles.emptyContainer, { paddingTop: screenHeight * 0.2 }]}>
					<Ionicons
						name="terminal-outline"
						size={64}
						color={theme.colors.surfaceVariant}
					/>
					<Text variant="headlineSmall" style={{ fontWeight: '700', marginTop: 16 }}>
						No commands found
					</Text>
					<Text variant="bodyLarge" style={{ color: theme.colors.onSurfaceVariant, marginTop: 8, textAlign: 'center' }}>
						Try typing `/new` to see available creation commands
					</Text>
				</View>
			);
		}

		return (
			<View style={[listStyles.emptyContainer, { paddingTop: screenHeight * 0.2 }]}>
				<Ionicons
					name="search-outline"
					size={64}
					color={theme.colors.surfaceVariant}
				/>
				<Text variant="headlineSmall" style={{ fontWeight: '700', marginTop: 16 }}>
					{loading ? 'Searching...' : searchQuery.length < 2 ? 'Start typing to search' : 'No results found'}
				</Text>
				<Text variant="bodyLarge" style={{ color: theme.colors.onSurfaceVariant, marginTop: 8, textAlign: 'center' }}>
					{loading ? 'Please wait...' : searchQuery.length < 2 ? 'Search across work orders, associations, users, and more' : 'Try a different search term'}
				</Text>
			</View>
		);
	};

	return (
		<Modal
			visible={isSearchVisible}
			onDismiss={handleClose}
			contentContainerStyle={{
				flex: 1,
				width: isLargeScreen ? '90%' : '100%',
				backgroundColor: theme.colors.background,
				margin: 0,
				padding: 0,
			}}
		>
			<View style={{ flex: 1 }}>
				{/* Search Header */}
				<View style={{
					borderBottomWidth: 1,
					borderBottomColor: theme.colors.outline,
					backgroundColor: theme.colors.surface,
					margin: 0,
					padding: 10,
				}}>
					<Searchbar
						placeholder={isCommandMode ? 'Type commands (e.g., /new)' : 'Search work orders, associations, users...'}
						onChangeText={handleSearchChange}
						value={searchQuery}
						style={{
							flex: 1,
							marginRight: 12,
							minWidth: 300,
						}}
						autoFocus
						onKeyPress={handleKeyPress}
					/>
					<TouchableOpacity
						onPress={handleClose}
						style={{
							position: 'absolute',
							top: -10,
							right: -10,
							zIndex: 10,
							padding: 8,
							borderRadius: 20,
							backgroundColor: theme.colors.surfaceVariant,
							elevation: 2,
						}}
					>
						<Ionicons
							name="close"
							size={20}
							color={theme.colors.onSurfaceVariant}
						/>
					</TouchableOpacity>
				</View>

				{/* Search Results */}
				<FlatList<SearchItem>
					data={isCommandMode ? commandResults : results}
					keyExtractor={(item, index) => {
						if (isCommandMode) {
							return `command-${(item as Command).id}-${index}`;
						}
						return `${(item as GlobalSearchResult).type}-${(item as GlobalSearchResult).id}-${index}`;
					}}
					renderItem={({ item, index }) => {
						if (isCommandMode) {
							return (
								<CommandResultItem
									command={item as Command}
									isSelected={index === selectedIndex}
									onPress={() => handleCommandPress(item as Command)}
								/>
							);
						}
						return (
							<SearchResultItem
								result={item as GlobalSearchResult}
								isSelected={index === selectedIndex}
								onPress={() => handleItemPress(item as GlobalSearchResult)}
							/>
						);
					}}
					refreshControl={
						<EnhancedRefreshControl
							refreshing={refreshing}
							onRefresh={handleRefresh}
							tintColor={theme.colors.primary}
						/>
					}
					ListEmptyComponent={<EmptyState />}
					style={{ flex: 1 }}
					contentContainerStyle={{ flexGrow: 1 }}
					keyboardShouldPersistTaps="handled"
					showsVerticalScrollIndicator={false}
				/>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	listItem: {
		padding: 16,
		borderBottomWidth: 1,
		borderBottomColor: '#e0e0e0',
	},
	listItemContent: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	listItemIcon: {
		marginRight: 12,
		width: 40,
		height: 40,
		borderRadius: 20,
		backgroundColor: '#f5f5f5',
		justifyContent: 'center',
		alignItems: 'center',
	},
	listItemText: {
		flex: 1,
	},
});
